import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { APP_ROUTES } from './app.routes';
import { AppAuthenticationService, AuthInterceptor } from '@cumlaude/shared-authentication';
import { LoadingInterceptor } from './core/loading-interceptor';
import { DashboardNameInterceptor } from './core/dashboard-name-interceptor';
import { ServerErrorInterceptor } from './core/server-error-interceptor';
import { HttpCancelInterceptor } from './core/http-cancel-interceptor';
import { BugsnagService } from '@cumlaude/bugsnag';
import { UserBugsnagService } from '@cumlaude/user-bugsnag';
import { UserAuthenticationService } from './services/user-authentication.service';
import { ParamEncodingInterceptor } from '@cumlaude/shared-utils';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { AppMenuService, AppRoutesService, MENU_NAVIGATION_TRACKER, MenuNavigationTracker } from '@cumlaude/shared-components-menu';
import { UserMenuService } from './services/user-menu.service';
import { DataRequestInterceptor } from './core/data-request-interceptor';
import { UserRoutesService } from './services/user-routes.service';
import { DashboardNavigatieService } from './services/dashboard-navigatie.service';

export const createAppConfig = (envConfig: EnvConfiguration): ApplicationConfig => ({
	providers: [
		{ provide: ENV_CONFIG, useValue: envConfig },
		importProvidersFrom(BrowserModule),
		provideZoneChangeDetection(),
		provideOAuthClient(),
		{
			provide: HTTP_INTERCEPTORS,
			multi: true,
			useClass: AuthInterceptor,
		},
		{
			provide: BugsnagService,
			useClass: UserBugsnagService,
		},
		{
			provide: ErrorHandler,
			useFactory(envConfig: EnvConfiguration) {
				if (envConfig.bugsnagApiKey) {
					return new BugsnagErrorHandler();
				}
				return new ErrorHandler();
			},
			deps: [ENV_CONFIG],
		},
		{
			provide: AppAuthenticationService,
			useClass: UserAuthenticationService,
		},
		{
			provide: AppMenuService,
			useClass: UserMenuService,
		},
		{
			provide: AppRoutesService,
			useClass: UserRoutesService,
		},
		{
			provide: MENU_NAVIGATION_TRACKER,
			useFactory: (dashboardNavigatie: DashboardNavigatieService): MenuNavigationTracker => ({
				trackMenuNavigation: (bestemming) => dashboardNavigatie.track('menu', bestemming),
			}),
			deps: [DashboardNavigatieService],
		},
		{
			provide: HTTP_INTERCEPTORS,
			multi: true,
			useClass: LoadingInterceptor,
		},
		{
			provide: HTTP_INTERCEPTORS,
			multi: true,
			useClass: DashboardNameInterceptor,
		},
		{
			provide: HTTP_INTERCEPTORS,
			multi: true,
			useClass: ServerErrorInterceptor,
		},
		{
			multi: true,
			provide: HTTP_INTERCEPTORS,
			useClass: HttpCancelInterceptor,
		},
		{
			multi: true,
			provide: HTTP_INTERCEPTORS,
			useClass: ParamEncodingInterceptor,
		},
		{
			multi: true,
			provide: HTTP_INTERCEPTORS,
			useClass: DataRequestInterceptor,
		},
		{
			provide: LOCALE_ID,
			useValue: 'nl-NL',
		},
		provideHttpClient(withInterceptorsFromDi(), withFetch()),
		provideRouter(APP_ROUTES, withComponentInputBinding()),
	],
});
